import React from "react";
import { TreeTable } from "primereact/treetable";
import { Column } from "primereact/column";
import { Tooltip } from "primereact/tooltip";
import PropTypes from "prop-types";
import ReactCountryFlag from "react-country-flag";
import CustomCheckBox from "../../../components/CheckBox/index";

const ServiceAccessTable = ({
  entityaccess,
  paginationOnchange,
  paginationFirst,
  paginationRows,
  role,
}) => {
  const isGxmAdmin = role === "GXM_ADMIN" || role === "SUPER_ADMIN";
  const isCustomer = role === "CUSTOMER" || role === "CUSTOMER_MANAGER";

  const buildEntityNode = (entity) => ({
    key: `entity-${entity.id}`,
    data: {
      name: (
        <span>
          <ReactCountryFlag countryCode={entity.country} svg className="mr-2" />
          {`${entity.name} (${entity.country})`}
        </span>
      ),
      status: null,
    },
    children: entity.services?.map((service) => {
      const children = service.process_templates?.map((process) => ({
        key: `process-${process.id}`,
        data: {
          name: process.name,
          status: (
            <div className="flex text-center checkbox-margin">
              <CustomCheckBox inputId={`checkbox-${process.id}`} checked />
            </div>
          ),
        },
      }));

      return {
        key: `service-${service.id}`,
        data: {
          name: service.name,
          status: null,
        },
        children,
      };
    }),
  });

  const convertToTreeNodes = (data) => {
    // Customer User/Manager: flat entity list (no tenant grouping)
    // Entity → Service → Process Templates
    if (isCustomer) {
      return data.map((entity) => buildEntityNode(entity));
    }

    // Group entities by tenant
    const tenantMap = {};

    data.forEach((entity) => {
      const tenantId = entity.tenant_id || "unknown";
      const tenantData = entity.tenantData || {
        id: tenantId,
        tenant_name: "Unknown Tenant",
      };

      if (!tenantMap[tenantId]) {
        tenantMap[tenantId] = {
          id: tenantId,
          name: tenantData.tenant_name || "Unknown Tenant",
          data: tenantData,
          entities: [],
        };
      }
      tenantMap[tenantId].entities.push(entity);
    });

    // GXM Admin / Super Admin: show only tenant list (no entities/services)
    if (isGxmAdmin) {
      return Object.values(tenantMap).map((tenant) => ({
        key: `tenant-${tenant.id}`,
        data: {
          name: <span>{tenant.name}</span>,
          status: null,
        },
      }));
    }

    // LSP User/Manager & GXM User/Manager: full tree with tenants → entities → services → process templates
    return Object.values(tenantMap).map((tenant) => ({
      key: `tenant-${tenant.id}`,
      data: {
        name: <span>{tenant.name}</span>,
        status: null,
      },
      children: tenant.entities.map((entity) => buildEntityNode(entity)),
    }));
  };

  const treeNodes = convertToTreeNodes(entityaccess || []);

  return (
    <div className="user-access-table">
      <TreeTable
        value={treeNodes}
        emptyMessage="No results were found"
        responsiveLayout="scroll"
        paginator={entityaccess?.length > 10}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        rowsPerPageOptions={[10, 20, 30]}
        first={paginationFirst}
        rows={paginationRows}
        onPage={paginationOnchange}
      >
        <Column
          field="name"
          header={
            isGxmAdmin ? (
              <span>
                Account Name{" "}
                <i
                  className="pi pi-info-circle info-icon"
                  id="gxm-admin-myaccount-info"
                />
                <Tooltip
                  target="#gxm-admin-myaccount-info"
                  content="GXM Admin users inherit full visibility across all entities, processes, and services within the tenant."
                  position="right"
                  className="gxm-admin-tooltip"
                />
              </span>
            ) : (
              "Account Name"
            )
          }
          expander={!isGxmAdmin}
          className="table-entity-column"
        />
      </TreeTable>
    </div>
  );
};

ServiceAccessTable.propTypes = {
  entityaccess: PropTypes.array,
  paginationFirst: PropTypes.number,
  paginationRows: PropTypes.number,
  paginationOnchange: PropTypes.func,
  role: PropTypes.string,
};
export default ServiceAccessTable;
